import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Check, ChefHat, Package, Truck, Home as HomeIcon, AlertCircle } from "lucide-react";
import { API } from "../lib/api";


const STATUSES = [
  { id: "received", label: "Order Received", icon: Check, blurb: "We've got your order — thanks for trusting us." },
  { id: "baking", label: "Baking", icon: ChefHat, blurb: "Your treats are in the oven, smelling incredible." },
  { id: "packaging", label: "Packaging", icon: Package, blurb: "Wrapping with care in our signature box." },
  { id: "out_for_delivery", label: "Out For Delivery", icon: Truck, blurb: "On the road and headed your way." },
  { id: "delivered", label: "Delivered", icon: HomeIcon, blurb: "Enjoy! Don't forget to tag us on Instagram." },
];

export default function OrderTracking() {
  const { state } = useLocation();
  const [orderNumber, setOrderNumber] = useState(state?.orderNumber || "");
  const [email, setEmail] = useState(state?.email || "");
  const [status, setStatus] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStatus = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    setStatus(null);
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/orders/${orderNumber.trim()}/status`, {
        params: { email: email.trim() },
      });
      setStatus(data.status);
      setOrder(data);
    } catch (err) {
      setError("We couldn't find an order with that number and email. Double-check and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch if we arrived with state
  useEffect(() => {
    if (state?.orderNumber && state?.email) {
      fetchStatus();
    }
    // eslint-disable-next-line
  }, []);

  const currentIndex = status ? STATUSES.findIndex((s) => s.id === status) : -1;

  return (
    <div className="bg-shay-cream min-h-screen pt-32 pb-24" data-testid="tracking-page">
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <div className="text-center mb-12">
          <p className="eyebrow">Where Is My Order?</p>
          <h1 className="font-display text-5xl md:text-6xl text-shay-navy mt-4 tracking-tight">
            Track your <span className="italic text-shay-gold">treats.</span>
          </h1>
          <p className="mt-5 max-w-lg mx-auto text-shay-chocolate/75">
            Enter your order number and email below to see where your cookies are right now.
          </p>
        </div>

        <form onSubmit={fetchStatus} className="bg-white border border-shay-gold/30 p-7 md:p-10 grid md:grid-cols-12 gap-5" data-testid="tracking-form">
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-widest text-shay-brown">Order Number</label>
            <input
              required
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder="SC-XXXXXXXX"
              className="field-input mt-1"
              data-testid="tracking-order-number"
            />
          </div>
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase tracking-widest text-shay-brown">Email Address</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="field-input mt-1"
              data-testid="tracking-email"
            />
          </div>
          <div className="md:col-span-2 flex md:items-end">
            <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60" data-testid="tracking-submit-btn">
              {loading ? "..." : "Track"}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-6 flex items-start gap-3 bg-shay-brown/10 border border-shay-brown/30 p-5 text-shay-chocolate" data-testid="tracking-error">
            <AlertCircle size={20} className="shrink-0 mt-0.5 text-shay-brown" />
            <p className="text-sm leading-relaxed">{error}</p>
          </div>
        )}

        {status && (
          <div className="mt-12" data-testid="tracking-results">
            <div className="text-center mb-10">
              <p className="eyebrow">Status</p>
              <h2 className="font-display text-3xl md:text-4xl text-shay-navy mt-2 tracking-tight">
                {STATUSES[currentIndex]?.label}
              </h2>
              <p className="mt-3 text-shay-chocolate/75 max-w-md mx-auto">
                {STATUSES[currentIndex]?.blurb}
              </p>
            </div>

            {/* Progress */}
            <ol className="relative grid grid-cols-5 gap-2 md:gap-4">
              <div className="absolute top-6 left-[10%] right-[10%] h-px bg-shay-navy/15" aria-hidden="true" />
              <div
                className="absolute top-6 left-[10%] h-px bg-shay-gold transition-all duration-700"
                style={{ width: `${(currentIndex / (STATUSES.length - 1)) * 80}%` }}
                aria-hidden="true"
              />
              {STATUSES.map((s, i) => {
                const Icon = s.icon;
                const done = i <= currentIndex;
                return (
                  <li key={s.id} className="relative flex flex-col items-center text-center" data-testid={`status-step-${s.id}`}>
                    <div
                      className={`h-12 w-12 grid place-items-center rounded-full transition-all duration-500 ${
                        done ? "bg-shay-gold text-shay-navy scale-100" : "bg-white border border-shay-navy/15 text-shay-navy/30 scale-95"
                      }`}
                    >
                      <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <p className={`mt-3 text-[10px] uppercase tracking-widest leading-tight ${done ? "text-shay-navy font-medium" : "text-shay-navy/40"}`}>
                      {s.label}
                    </p>
                  </li>
                );
              })}
            </ol>

            {order?.estimated_delivery && (
              <p className="mt-12 text-center text-sm text-shay-chocolate/70" data-testid="tracking-eta">
                Estimated delivery: <span className="text-shay-navy font-medium">{order.estimated_delivery}</span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
